import { safeJsonParse } from '../../../utils/safeJsonParse.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-radius: var(--ctl-radius-lg, 14px);
    --ctl-shadow: var(--ctl-shadow-sm, 0 6px 18px rgba(0,0,0,.10));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .frame{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius);
    box-shadow: var(--ctl-shadow);
    overflow: hidden;
  }

  .header{
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }
  .header:empty{ display:none; }

  .scroller{
    height: var(--ctl-virtual-height, 420px);
    overflow: auto;
    background: var(--ctl-color-bg);
    position: relative;
  }

  .pad{
    height: var(--_pad, 0px);
    position: relative;
  }

  .items{
    position: absolute;
    left: 0;
    right: 0;
    top: var(--_top, 0px);
    display: block;
  }

  .row{
    height: var(--_rowH, 44px);
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2-5);
    padding: 0 var(--ctl-space-3);
    border-bottom: 1px solid color-mix(in srgb, var(--ctl-color-border) 70%, transparent);
    background: var(--ctl-color-surface);
    min-width: 0;
  }

  .row[data-clickable="true"]{
    cursor: pointer;
  }
  .row:hover{
    background: color-mix(in srgb, var(--ctl-color-brand) 6%, var(--ctl-color-surface));
  }

  /* Hide the template used for rendering */
  ::slotted(template[slot="item"]){ display:none; }

  .empty{
    padding: var(--ctl-space-4-5);
    color: var(--ctl-color-muted);
    text-align:center;
  }

  .footer{
    border-top: 1px solid var(--ctl-color-border);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
    color: var(--ctl-color-muted);
    background: var(--ctl-color-surface);
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    font-size: var(--ctl-font-size-md);
  }

  .spinner{
    width: var(--ctl-space-4-5); height: var(--ctl-space-4-5);
    border-radius: var(--ctl-radius-full);
    border: var(--ctl-space-0-5) solid color-mix(in srgb, var(--ctl-color-border) 85%, transparent);
    border-top-color: var(--ctl-color-brand);
    animation: spin .8s linear infinite;
  }
  @keyframes spin{ to{ transform: rotate(360deg); } }

  .srOnly{
    position:absolute;
    width:1px; height:1px;
    padding:0; margin:calc(-1 * var(--ctl-space-0-5));
    overflow:hidden; clip: rect(0,0,0,0);
    white-space:nowrap; border:0;
  }
</style>

<div class="frame" part="frame">
  <div class="header" part="header"><slot name="header"></slot></div>

  <div class="scroller" part="scroller" id="scroller" aria-busy="false">
    <div id="empty" class="empty" part="empty" hidden>
      <slot name="empty">No results.</slot>
    </div>

    <div class="pad" id="pad">
      <div class="items" id="items"></div>
    </div>

    <slot name="item" id="itemSlot"></slot>
    <div class="srOnly" aria-live="polite" id="live"></div>
  </div>

  <div class="footer" part="footer" id="footer">
    <slot name="footer"></slot>
  </div>
</div>
`;

function toInt(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toArray(v) {
  return Array.isArray(v) ? v : [];
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

export class CtlVirtualList extends HTMLElement {
  static get observedAttributes() {
    return ["items", "item-height", "overscan", "threshold", "has-more", "loading", "key-field", "clickable"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._scroller = this.shadowRoot.getElementById("scroller");
    this._pad = this.shadowRoot.getElementById("pad");
    this._itemsEl = this.shadowRoot.getElementById("items");
    this._emptyEl = this.shadowRoot.getElementById("empty");
    this._footer = this.shadowRoot.getElementById("footer");
    this._itemSlot = this.shadowRoot.getElementById("itemSlot");
    this._live = this.shadowRoot.getElementById("live");

    this._items = [];
    this._tmpl = null;

    this._rowH = 44;
    this._overscan = 8;
    this._threshold = 320;
    this._keyField = "id";

    this._range = { start: 0, end: -1 };
    this._raf = 0;

    this._loadGate = { pending: false, lastAt: 0, lastCount: 0 };

    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    // hydrate attrs
    if (this.hasAttribute("items")) this.items = safeJsonParse(this.getAttribute("items"), []);
    if (this.hasAttribute("item-height")) this.itemHeight = this.getAttribute("item-height");
    if (this.hasAttribute("overscan")) this.overscan = this.getAttribute("overscan");
    if (this.hasAttribute("threshold")) this.threshold = this.getAttribute("threshold");
    if (this.hasAttribute("key-field")) this.keyField = this.getAttribute("key-field");

    if (!this.hasAttribute("overscan")) this.setAttribute("overscan", String(this._overscan));
    if (!this.hasAttribute("threshold")) this.setAttribute("threshold", String(this._threshold));
    if (!this.hasAttribute("item-height")) this.setAttribute("item-height", String(this._rowH));
    if (!this.hasAttribute("key-field")) this.setAttribute("key-field", this._keyField);

    this._itemSlot.addEventListener("slotchange", this._onSlotChange);
    this._scroller.addEventListener("scroll", this._onScroll, { passive: true });
    this.shadowRoot.addEventListener("click", this._onClick);

    // Observe size changes to recompute visible range
    this._ro = new ResizeObserver(this._onResize);
    this._ro.observe(this._scroller);

    this._collectTemplate();
    this._render(true);
  }

  disconnectedCallback() {
    this._itemSlot.removeEventListener("slotchange", this._onSlotChange);
    this._scroller.removeEventListener("scroll", this._onScroll);
    this.shadowRoot.removeEventListener("click", this._onClick);
    if (this._ro) this._ro.disconnect();
    cancelAnimationFrame(this._raf);
  }

  attributeChangedCallback(name) {
    if (name === "items") this.items = safeJsonParse(this.getAttribute("items"), []);
    if (name === "item-height") this.itemHeight = this.getAttribute("item-height");
    if (name === "overscan") this.overscan = this.getAttribute("overscan");
    if (name === "threshold") this.threshold = this.getAttribute("threshold");
    if (name === "key-field") this.keyField = this.getAttribute("key-field");
    if (name === "loading") this._syncBusy();
    if (name === "has-more") this._syncFooter();
  }

  // ---- Props ----
  get items() { return this._items; }
  set items(v) {
    const newItems = toArray(v);
    const newJson = JSON.stringify(newItems);
    const oldJson = JSON.stringify(this._items || []);
    if (newJson === oldJson) return;
    this._items = newItems;
    this.setAttribute("items", newJson);
    this._loadGate.lastCount = this._items.length;
    this._render(true);
  }

  get itemHeight() { return this._rowH; }
  set itemHeight(v) {
    const n = toInt(v, 44);
    const newValue = Math.max(18, n);
    if (this._rowH === newValue) return;
    this._rowH = newValue;
    this.setAttribute("item-height", String(this._rowH));
    this._render(true);
  }

  get overscan() { return this._overscan; }
  set overscan(v) {
    const newValue = clamp(toInt(v, 8), 0, 50);
    if (this._overscan === newValue) return;
    this._overscan = newValue;
    this.setAttribute("overscan", String(this._overscan));
    this._render(true);
  }

  get threshold() { return this._threshold; }
  set threshold(v) {
    const newValue = clamp(toInt(v, 320), 0, 5000);
    if (this._threshold === newValue) return;
    this._threshold = newValue;
    this.setAttribute("threshold", String(this._threshold));
  }

  get hasMore() { return this.hasAttribute("has-more"); }
  set hasMore(v) { v ? this.setAttribute("has-more", "") : this.removeAttribute("has-more"); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get keyField() { return this._keyField; }
  set keyField(v) {
    const newValue = String(v || "id");
    if (this._keyField === newValue) return;
    this._keyField = newValue;
    this.setAttribute("key-field", this._keyField);
    this._render(true);
  }

  get clickable() { return this.hasAttribute("clickable"); }
  set clickable(v) { v ? this.setAttribute("clickable", "") : this.removeAttribute("clickable"); }

  // ---- Template slot ----
  _onSlotChange() {
    this._collectTemplate();
    this._render(true);
  }

  _collectTemplate() {
    const assigned = this._itemSlot.assignedElements({ flatten: true }) || [];
    const tmpl = assigned.find((el) => el.tagName === "TEMPLATE") || null;
    this._tmpl = tmpl;
  }

  // ---- Render ----
  _onScroll() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      this._render(false);
      this._maybeLoadMore();
    });
  }

  _onResize() {
    this._render(true);
    this._maybeLoadMore();
  }

  _syncBusy() {
    this._scroller.setAttribute("aria-busy", this.loading ? "true" : "false");
    this._syncFooter();
  }

  _syncFooter() {
    // If the consumer provides footer slot content, leave it alone.
    // If footer slot is empty, show a default footer that reflects state.
    const footerSlot = this.shadowRoot.querySelector('slot[name="footer"]');
    const hasFooterSlotContent = (footerSlot?.assignedNodes({ flatten: true }) || [])
      .some((n) => (n.nodeType === 1) || (n.nodeType === 3 && String(n.textContent).trim()));

    if (hasFooterSlotContent) return;

    const hasMore = this.hasMore;
    const loading = this.loading;

    const left = `<span>${escapeHtml(this._items.length)} items</span>`;
    let right = "";

    if (loading) right = `<span style="display:inline-flex; gap:var(--ctl-space-2); align-items:center;"><span class="spinner" aria-hidden="true"></span>Loading…</span>`;
    else if (hasMore) right = `<span>Scroll to load more</span>`;
    else right = `<span>End of results</span>`;

    this._footer.innerHTML = `${left}<span style="margin-left:auto;">${right}</span>`;
  }

  _render(force) {
    this._syncBusy();
    this._syncFooter();

    const items = this._items;
    const rowH = this._rowH;

    // empty
    const empty = items.length === 0 && !this.loading;
    this._emptyEl.hidden = !empty;
    this._pad.style.display = empty ? "none" : "block";

    if (empty) {
      this._itemsEl.innerHTML = "";
      return;
    }

    // PHASE 1: Read all DOM properties first (prevent layout thrashing)
    const scrollTop = this._scroller.scrollTop;
    const viewportH = this._scroller.clientHeight || 1;

    // PHASE 2: Calculate everything
    const totalH = items.length * rowH;
    const start = clamp(Math.floor(scrollTop / rowH) - this._overscan, 0, Math.max(0, items.length - 1));
    const end = clamp(Math.ceil((scrollTop + viewportH) / rowH) + this._overscan, 0, items.length - 1);

    if (!force && start === this._range.start && end === this._range.end) return;
    this._range = { start, end };

    const topPx = start * rowH;

    // PHASE 3: Write all DOM changes at once
    this._pad.style.setProperty("--_pad", `${totalH}px`);
    this._pad.style.setProperty("--_rowH", `${rowH}px`);
    this._pad.style.setProperty("--_top", `${topPx}px`);

    // PHASE 4: Update content (render visible rows)
    const slice = items.slice(start, end + 1);

    // If no template provided, render JSON-ish default
    if (!this._tmpl) {
      this._itemsEl.innerHTML = slice.map((item, i) => {
        const index = start + i;
        const key = item?.[this._keyField] ?? index;
        return `
          <div class="row" data-index="${index}" data-key="${escapeHtml(String(key))}" data-clickable="${this.clickable ? "true" : "false"}">
            <span class="muted" style="min-width:var(--ctl-space-12);">#${index + 1}</span>
            <span style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(JSON.stringify(item))}</span>
          </div>
        `;
      }).join("");
      return;
    }

    this._itemsEl.innerHTML = slice.map((item, i) => {
      const index = start + i;
      const key = item?.[this._keyField] ?? index;
      const html = this._renderTemplate(this._tmpl, { item, index, key });
      return `
        <div class="row" data-index="${index}" data-key="${escapeHtml(String(key))}" data-clickable="${this.clickable ? "true" : "false"}">
          ${html}
        </div>
      `;
    }).join("");
  }

  _renderTemplate(tmpl, ctx) {
    // Very small template engine:
    // - {{index}} {{key}}
    // - {{item.field}} for direct fields
    // - {{json}} gives JSON string
    const raw = tmpl.innerHTML;

    const json = JSON.stringify(ctx.item);
    return raw
      .replace(/\{\{\s*json\s*\}\}/g, escapeHtml(json))
      .replace(/\{\{\s*index\s*\}\}/g, escapeHtml(String(ctx.index)))
      .replace(/\{\{\s*key\s*\}\}/g, escapeHtml(String(ctx.key)))
      .replace(/\{\{\s*item\.([a-zA-Z0-9_]+)\s*\}\}/g, (_, field) => {
        const v = ctx.item ? ctx.item[field] : "";
        return escapeHtml(v == null ? "" : String(v));
      });
  }

  // ---- Infinite load ----
  _maybeLoadMore() {
    if (!this.hasMore) return;
    if (this.loading) return;

    const now = Date.now();
    // Gate to avoid spamming when near bottom and scroll events fire rapidly
    if (this._loadGate.pending) return;
    if (now - this._loadGate.lastAt < 250) return;

    const el = this._scroller;
    const remaining = (el.scrollHeight - el.scrollTop - el.clientHeight);

    if (remaining <= this._threshold) {
      this._loadGate.pending = true;
      this._loadGate.lastAt = now;

      const cursor = this._items.length ? (this._items[this._items.length - 1]?.[this._keyField] ?? this._items.length - 1) : null;

      this.dispatchEvent(new CustomEvent("ctl-load-more", {
        bubbles: true,
        composed: true,
        detail: { cursor, count: this._items.length, reason: "threshold" }
      }));

      // allow next trigger once items length changes OR after small delay
      setTimeout(() => {
        this._loadGate.pending = false;
      }, 500);
    }
  }

  // ---- Click handling ----
  _onClick(e) {
    if (!this.clickable) return;

    const path = e.composedPath ? e.composedPath() : [];
    const row = path.find((n) => n?.classList?.contains("row"));
    if (!row) return;

    const index = Number(row.dataset.index);
    if (!Number.isFinite(index)) return;

    const item = this._items[index];

    this.dispatchEvent(new CustomEvent("ctl-item-click", {
      bubbles: true,
      composed: true,
      detail: { index, item }
    }));
  }
}

customElements.define("ctl-virtual-list", CtlVirtualList);
